import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Column } from '../../components/DataTable/DataTable';
import {
  DocumentTextIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  PlusIcon,
  Select,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import type { SpareCase } from '../spares/spare-cases/useData';
import {
  getColumns as getSpareCaseColumns,
  getTableActions as getSpareCaseTableActions,
  moduleConfig as spareCasesConfig,
} from '../spares/spare-cases/config';
import type { SpareRequisition } from '../spares/requisitions/useData';
import {
  getColumns as getSpareRequisitionColumns,
  getTableActions as getSpareRequisitionTableActions,
  moduleConfig as spareRequisitionsConfig,
} from '../spares/requisitions/requisitionsModuleConfig';
import type { SupplyCase } from '../supplies/supply-cases/useData';
import {
  getColumns as getSupplyCaseColumns,
  getTableActions as getSupplyCaseTableActions,
  moduleConfig as supplyCasesConfig,
} from '../supplies/supply-cases/config';
import type { SupplyRequisition } from '../supplies/requisitions/useData';
import {
  getColumns as getSupplyRequisitionColumns,
  getTableActions as getSupplyRequisitionTableActions,
  moduleConfig as supplyRequisitionsConfig,
} from '../supplies/requisitions/requisitionsModuleConfig';

type PortCallsScope = 'all' | 'supplies' | 'spares';
type ModuleId = 'supplies' | 'spares';
type ListTab = 'all' | 'supplies' | 'spares';

export type TaggedCase = (SupplyCase | SpareCase) & { moduleId: ModuleId };
export type TaggedReq = (SupplyRequisition | SpareRequisition) & { moduleId: ModuleId };

export type PortCallCreateAction = {
  href: string;
  label: string;
  title?: string;
  primary?: boolean;
};

type Props = {
  title: string;
  mode: 'cases' | 'requisitions';
  scope: PortCallsScope;
  cases: TaggedCase[];
  requisitions: TaggedReq[];
  isLoading?: boolean;
  portCallsPath: string;
  createActions: PortCallCreateAction[];
  onPersistUi: () => void;
  onDeleteCase: (id: number, moduleId: ModuleId) => void;
  onDeleteRequisition: (id: number, moduleId: ModuleId) => void;
};

const MODULE_BADGE: Record<ModuleId, string> = {
  supplies: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200',
  spares: 'bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-200',
};

function moduleLabel(id: ModuleId) {
  return id === 'spares' ? 'Spares' : 'Supplies';
}

function caseStatusLabel(row: TaggedCase) {
  return row.status?.name?.trim() || '';
}

function reqStatusLabel(row: TaggedReq) {
  const s = (row as SupplyRequisition).saveStatus;
  if (s === 'DRAFT') return 'Draft';
  if (s === 'SAVED') return 'Saved';
  return s || '';
}

function withoutTreeCaseColumns<T>(cols: Column<T>[]): Column<T>[] {
  return cols.filter((c) => {
    const key = String(c.key);
    const header = String(c.header ?? '').toLowerCase();
    if (key === 'vesselId' || key === 'vessel.vesselName' || header.includes('vessel')) return false;
    if (key === 'eta' || header === 'eta') return false;
    return true;
  });
}

function withoutTreeReqColumns<T>(cols: Column<T>[]): Column<T>[] {
  return cols.filter((c) => {
    const key = String(c.key);
    const header = String(c.header ?? '').toLowerCase();
    if (key === 'vessel.vesselName' || header.includes('vessel')) return false;
    return true;
  });
}

function cellValue(row: Record<string, unknown>, key: string): unknown {
  if (!key.includes('.')) return row[key];
  return key.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[part];
    return undefined;
  }, row);
}

function caseSortValue(row: TaggedCase, key: string): string {
  switch (key) {
    case 'module':
      return moduleLabel(row.moduleId);
    case 'ref':
      return String(row.ref ?? '').toLowerCase();
    case 'caseDate':
      return String(row.caseDate ?? '');
    case 'status':
    case 'statusId':
      return caseStatusLabel(row).toLowerCase();
    case 'categories':
      return (row.categories ?? [])
        .map((c) => c.name ?? '')
        .join(' ')
        .toLowerCase();
    case 'createdAt':
      return String(row.createdAt ?? '');
    default:
      return String(cellValue(row as unknown as Record<string, unknown>, key) ?? '').toLowerCase();
  }
}

function reqSortValue(row: TaggedReq, key: string): string {
  switch (key) {
    case 'module':
      return moduleLabel(row.moduleId);
    case 'ref':
      return String(row.ref ?? '').toLowerCase();
    case 'reqDate':
      return String(row.reqDate ?? '');
    case 'status':
    case 'saveStatus':
      return reqStatusLabel(row).toLowerCase();
    case 'categories':
      return (row.categories ?? [])
        .map((c) => c.name ?? '')
        .join(' ')
        .toLowerCase();
    case 'lines':
      return String(
        (row as SupplyRequisition)._count?.lines ?? (row as SupplyRequisition).lines?.length ?? 0,
      );
    case 'createdAt':
      return String(row.createdAt ?? '');
    default:
      return String(cellValue(row as unknown as Record<string, unknown>, key) ?? '').toLowerCase();
  }
}

function columnWidthStyle(col: Column<unknown>): React.CSSProperties | undefined {
  const key = String(col.key);
  // Shrink-wrap text columns so maxWidth caps do not leave a wide empty gap.
  if (key === 'categories' || key === 'ref' || key === 'statusId' || key === 'saveStatus') {
    return {
      width: '1%',
      whiteSpace: 'nowrap',
      maxWidth: col.maxWidth,
    };
  }
  if (col.width) {
    return {
      width: col.width,
      minWidth: col.width,
      maxWidth: col.maxWidth ?? col.width,
    };
  }
  if (col.maxWidth) {
    return { maxWidth: col.maxWidth };
  }
  return undefined;
}

export const PortCallChildrenInbox: React.FC<Props> = ({
  title,
  mode,
  scope,
  cases,
  requisitions,
  isLoading,
  portCallsPath,
  createActions,
  onPersistUi,
  onDeleteCase,
  onDeleteRequisition,
}) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const returnState = { from: portCallsPath };

  const showModule = scope === 'all';
  const [tab, setTab] = useState<ListTab>('all');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    setTab('all');
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setPage(1);
  }, [title, mode]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const caseColumns = useMemo(() => {
    if (scope === 'spares') {
      return withoutTreeCaseColumns(getSpareCaseColumns(formatDate) as Column<TaggedCase>[]);
    }
    return withoutTreeCaseColumns(getSupplyCaseColumns(formatDate) as Column<TaggedCase>[]);
  }, [scope]);

  const reqColumns = useMemo(() => {
    if (scope === 'spares') {
      return withoutTreeReqColumns(getSpareRequisitionColumns(formatDate) as Column<TaggedReq>[]);
    }
    return withoutTreeReqColumns(getSupplyRequisitionColumns(formatDate) as Column<TaggedReq>[]);
  }, [scope]);

  const { sortConfig, handleSort, setSortConfig } = useGeneralListSort(
    mode === 'cases'
      ? { key: 'caseDate', direction: 'desc' }
      : { key: 'reqDate', direction: 'desc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'ref' ||
        key === 'status' ||
        key === 'statusId' ||
        key === 'saveStatus' ||
        key === 'categories' ||
        key === 'module'
          ? 'asc'
          : 'desc',
    },
  );

  useEffect(() => {
    setSortConfig(
      mode === 'cases'
        ? { key: 'caseDate', direction: 'desc' }
        : { key: 'reqDate', direction: 'desc' },
    );
  }, [mode, setSortConfig]);

  useEffect(() => {
    setPage(1);
  }, [search, tab, statusFilter, itemsPerPage, sortConfig.key, sortConfig.direction, mode]);

  const sourceCases = useMemo(() => {
    if (!showModule || tab === 'all') return cases;
    return cases.filter((c) => c.moduleId === tab);
  }, [cases, showModule, tab]);

  const sourceReqs = useMemo(() => {
    if (!showModule || tab === 'all') return requisitions;
    return requisitions.filter((r) => r.moduleId === tab);
  }, [requisitions, showModule, tab]);

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    if (mode === 'cases') {
      for (const row of sourceCases) {
        const s = caseStatusLabel(row);
        if (Boolean(s)) set.add(s);
      }
    } else {
      for (const row of sourceReqs) {
        const s = reqStatusLabel(row);
        if (Boolean(s)) set.add(s);
      }
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [mode, sourceCases, sourceReqs]);

  const filteredCases = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sourceCases.filter((row) => {
      if (statusFilter && caseStatusLabel(row) !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        row.ref,
        row.description,
        row.notes,
        row.vessel?.vesselName,
        caseStatusLabel(row),
        row.port?.name,
        ...(row.categories ?? []).map((c) => c.name),
        moduleLabel(row.moduleId),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [sourceCases, search, statusFilter]);

  const filteredReqs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sourceReqs.filter((row) => {
      if (statusFilter && reqStatusLabel(row) !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        row.ref,
        row.vessel?.vesselName,
        reqStatusLabel(row),
        ...(row.categories ?? []).map((c) => c.name),
        moduleLabel(row.moduleId),
        (row as SupplyRequisition).notes,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [sourceReqs, search, statusFilter]);

  const sortedCases = useMemo(() => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...filteredCases].sort(
      (a, b) =>
        caseSortValue(a, sortConfig.key).localeCompare(caseSortValue(b, sortConfig.key), undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * dir,
    );
  }, [filteredCases, sortConfig.key, sortConfig.direction]);

  const sortedReqs = useMemo(() => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...filteredReqs].sort(
      (a, b) =>
        reqSortValue(a, sortConfig.key).localeCompare(reqSortValue(b, sortConfig.key), undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * dir,
    );
  }, [filteredReqs, sortConfig.key, sortConfig.direction]);

  const sortedRows = mode === 'cases' ? sortedCases : sortedReqs;
  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  const counts = useMemo(() => {
    const list = mode === 'cases' ? cases : requisitions;
    return {
      all: list.length,
      supplies: list.filter((r) => r.moduleId === 'supplies').length,
      spares: list.filter((r) => r.moduleId === 'spares').length,
    };
  }, [mode, cases, requisitions]);

  const viewTabs = useMemo(() => {
    const noun = mode === 'cases' ? 'cases' : 'requisitions';
    const tabs: Array<{
      id: ListTab;
      label: string;
      icon: React.ReactNode;
      count: number;
      emptyTitle: string;
      emptyHint: string;
    }> = [
      {
        id: 'all',
        label: mode === 'cases' ? 'Cases' : 'Requisitions',
        icon: <DocumentTextIcon className="h-4 w-4" />,
        count: counts.all,
        emptyTitle: mode === 'cases' ? 'No cases here' : 'No pending requisitions',
        emptyHint:
          mode === 'cases'
            ? 'Cases for this port call appear here.'
            : 'Pending requisitions without a case appear here.',
      },
    ];
    if (showModule) {
      tabs.push(
        {
          id: 'supplies',
          label: 'Supplies',
          icon: <FolderIcon className="h-4 w-4" />,
          count: counts.supplies,
          emptyTitle: `No supply ${noun}`,
          emptyHint: `No supply ${noun} under this selection.`,
        },
        {
          id: 'spares',
          label: 'Spares',
          icon: <FolderIcon className="h-4 w-4" />,
          count: counts.spares,
          emptyTitle: `No spare ${noun}`,
          emptyHint: `No spare ${noun} under this selection.`,
        },
      );
    }
    return tabs;
  }, [mode, counts, showModule]);

  useEffect(() => {
    if (!viewTabs.some((t) => t.id === tab)) setTab('all');
  }, [viewTabs, tab]);

  const hasFilters = Boolean(search.trim() || statusFilter || (showModule && tab !== 'all'));
  const activeTabMeta = viewTabs.find((t) => t.id === tab) ?? viewTabs[0];
  const primaryCreate = createActions.find((a) => a.primary) ?? createActions[0];

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setTab('all');
  };

  const openCase = (row: TaggedCase) => {
    onPersistUi();
    const detailRoute =
      row.moduleId === 'spares' ? spareCasesConfig.module.route.detail : supplyCasesConfig.module.route.detail;
    navigate(detailRoute(row.id), { state: returnState });
  };

  const openReq = (row: TaggedReq) => {
    onPersistUi();
    const detailRoute =
      row.moduleId === 'spares'
        ? spareRequisitionsConfig.module.route.detail
        : supplyRequisitionsConfig.module.route.detail;
    navigate(detailRoute(row.id), { state: returnState });
  };

  const displayColumns = mode === 'cases' ? caseColumns : reqColumns;

  return (
    <GeneralList
      className="h-full min-h-0"
      title={title}
      tabs={viewTabs.map(({ id, label, icon, count }) => ({ id, label, icon, count }))}
      activeTab={tab}
      onTabChange={(value) => setTab(value as ListTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: mode === 'cases' ? 'Search cases…' : 'Search requisitions…',
        title: 'Searches reference, vessel, status, port, categories',
      }}
      actions={
        createActions.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            {createActions.map((action) => (
              <Link
                key={action.href}
                to={action.href}
                state={returnState}
                onClick={onPersistUi}
                title={action.title}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium shadow-sm',
                  action.primary || createActions.length === 1
                    ? cn('text-white', colors.primary)
                    : cn('border', colors.border, colors.text),
                )}
              >
                <PlusIcon className="h-4 w-4" />
                {action.label}
              </Link>
            ))}
          </div>
        ) : undefined
      }
      filters={
        <div className="flex flex-wrap gap-3">
          <div className="min-w-[160px] flex-1 basis-[160px] sm:max-w-[220px]">
            <Select
              value={statusFilter}
              options={statusOptions}
              placeholder="Status"
              showClearButton
              onChange={(e) => setStatusFilter(e.target.value || '')}
            />
          </div>
        </div>
      }
      resultSummary={
        !isLoading ? (
          <p className={cn('text-xs', colors.textMuted)}>
            {mode === 'cases'
              ? total === 1
                ? '1 case'
                : `${total} cases`
              : total === 1
                ? '1 requisition'
                : `${total} requisitions`}
            {hasFilters ? ' matching filters' : ''}
          </p>
        ) : (
          <span />
        )
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={Boolean(isLoading)}
      loadingLabel={mode === 'cases' ? 'Loading cases…' : 'Loading requisitions…'}
      isEmpty={pageRows.length === 0}
      emptyState={{
        icon: <DocumentTextIcon className="h-12 w-12" />,
        title: hasFilters
          ? mode === 'cases'
            ? 'No matching cases'
            : 'No matching requisitions'
          : activeTabMeta.emptyTitle,
        hint: hasFilters ? 'Try adjusting filters or clearing the search.' : activeTabMeta.emptyHint,
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : primaryCreate ? (
          <Link
            to={primaryCreate.href}
            state={returnState}
            onClick={onPersistUi}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            {primaryCreate.label}
          </Link>
        ) : undefined,
      }}
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-max border-separate border-spacing-0 text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th
              className={cn(
                'sticky left-0 z-20 whitespace-nowrap bg-inherit px-3 py-3 font-medium',
                colors.surface,
              )}
              style={{ minWidth: '5.5rem' }}
            />
            {showModule ? (
              <GeneralListSortableTh sortKey="module" className="whitespace-nowrap" style={{ minWidth: '6.5rem' }}>
                Module
              </GeneralListSortableTh>
            ) : null}
            {displayColumns.map((col) => {
              const key = String(col.key);
              const header = col.header;
              const style = columnWidthStyle(col as Column<unknown>);
              const align = col.align ?? 'left';
              if (col.sortable) {
                return (
                  <GeneralListSortableTh
                    key={key}
                    sortKey={key}
                    className="whitespace-nowrap"
                    align={align}
                    style={style}
                  >
                    {header}
                  </GeneralListSortableTh>
                );
              }
              return (
                <th
                  key={key}
                  className={cn(
                    'whitespace-nowrap px-3 py-3 font-medium',
                    align === 'center' && 'text-center',
                    align === 'right' && 'text-right',
                  )}
                  style={style}
                >
                  {header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {mode === 'cases'
            ? (pageRows as TaggedCase[]).map((row) => (
                <tr
                  key={`${row.moduleId}:${row.id}`}
                  onClick={() => openCase(row)}
                  className={cn(
                    'group cursor-pointer border-b transition last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                    colors.border,
                  )}
                >
                  <td
                    className={cn(
                      'sticky left-0 z-10 whitespace-nowrap px-3 py-3.5',
                      colors.surface,
                      'group-hover:bg-slate-50 dark:group-hover:bg-slate-800',
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.moduleId === 'spares'
                      ? getSpareCaseTableActions(
                          row as SpareCase,
                          (id) => onDeleteCase(id, 'spares'),
                          undefined,
                          undefined,
                          { returnPath: portCallsPath },
                        )
                      : getSupplyCaseTableActions(
                          row as SupplyCase,
                          (id) => onDeleteCase(id, 'supplies'),
                          undefined,
                          undefined,
                          { returnPath: portCallsPath },
                        )}
                  </td>
                  {showModule ? (
                    <td className="whitespace-nowrap px-3 py-3.5">
                      <span
                        className={cn(
                          'inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium',
                          MODULE_BADGE[row.moduleId],
                        )}
                      >
                        {moduleLabel(row.moduleId)}
                      </span>
                    </td>
                  ) : null}
                  {caseColumns.map((col) => {
                    const key = String(col.key);
                    const value = cellValue(row as unknown as Record<string, unknown>, key);
                    const align = col.align ?? 'left';
                    return (
                      <td
                        key={key}
                        className={cn(
                          'px-3 py-3.5 align-middle',
                          align === 'center' && 'text-center',
                          align === 'right' && 'text-right',
                        )}
                        style={columnWidthStyle(col as Column<unknown>)}
                      >
                        {col.render ? col.render(value, row) : (value as React.ReactNode) ?? ''}
                      </td>
                    );
                  })}
                </tr>
              ))
            : (pageRows as TaggedReq[]).map((row) => (
                <tr
                  key={`${row.moduleId}:${row.id}`}
                  onClick={() => openReq(row)}
                  className={cn(
                    'group cursor-pointer border-b transition last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                    colors.border,
                  )}
                >
                  <td
                    className={cn(
                      'sticky left-0 z-10 whitespace-nowrap px-3 py-3.5',
                      colors.surface,
                      'group-hover:bg-slate-50 dark:group-hover:bg-slate-800',
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.moduleId === 'spares'
                      ? getSpareRequisitionTableActions(
                          row as SpareRequisition,
                          (id) => onDeleteRequisition(id, 'spares'),
                          undefined,
                          undefined,
                          { returnPath: portCallsPath },
                        )
                      : getSupplyRequisitionTableActions(
                          row as SupplyRequisition,
                          (id) => onDeleteRequisition(id, 'supplies'),
                          undefined,
                          undefined,
                          { returnPath: portCallsPath },
                        )}
                  </td>
                  {showModule ? (
                    <td className="whitespace-nowrap px-3 py-3.5">
                      <span
                        className={cn(
                          'inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium',
                          MODULE_BADGE[row.moduleId],
                        )}
                      >
                        {moduleLabel(row.moduleId)}
                      </span>
                    </td>
                  ) : null}
                  {reqColumns.map((col) => {
                    const key = String(col.key);
                    const value = cellValue(row as unknown as Record<string, unknown>, key);
                    const align = col.align ?? 'left';
                    return (
                      <td
                        key={key}
                        className={cn(
                          'px-3 py-3.5 align-middle',
                          align === 'center' && 'text-center',
                          align === 'right' && 'text-right',
                        )}
                        style={columnWidthStyle(col as Column<unknown>)}
                      >
                        {col.render ? col.render(value, row) : (value as React.ReactNode) ?? ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
        </tbody>
      </table>
    </GeneralList>
  );
};
